import { useEffect, useRef, useState } from 'react';

interface UseResultScreenTimeoutOptions {
  /** True once the countdown may run (paired tablet, result recorded, timeout configured). */
  enabled: boolean;
  timeoutMs: number;
  /** Freezes the countdown (validation being sent, self-assessment form open); it resumes where it stopped. */
  paused: boolean;
  onExpire: () => void;
}

const TICK_MS = 1000;

/**
 * Countdown of a paired tablet on the result screen: once
 * it runs out, the tablet goes back to its waiting screen. Starts the first time `enabled`
 * turns true and counts wall-clock time between ticks, so a background tab catches up on
 * the next tick instead of drifting. `onExpire` fires once.
 *
 * @returns remainingMs Milliseconds left, or null while the countdown is not running
 */
export function useResultScreenTimeout({ enabled, timeoutMs, paused, onExpire }: UseResultScreenTimeoutOptions): { remainingMs: number | null } {
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const remainingRef = useRef<number | null>(null);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!enabled || timeoutMs <= 0 || expiredRef.current) return;
    if (remainingRef.current === null) {
      remainingRef.current = timeoutMs;
      setRemainingMs(timeoutMs);
    }
    if (paused) return;

    let lastTick = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      const left = Math.max(0, (remainingRef.current ?? 0) - (now - lastTick));
      lastTick = now;
      remainingRef.current = left;
      setRemainingMs(left);
      if (left === 0 && !expiredRef.current) {
        expiredRef.current = true;
        clearInterval(timer);
        onExpireRef.current();
      }
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [enabled, timeoutMs, paused]);

  return { remainingMs };
}

/** mm:ss of a duration, for the countdown shown to the referee. */
export function formatCountdown(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
